import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import mermaid from 'mermaid';
import './App.css';

const API_URL = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'development' ? 'http://localhost:5001' : '');

// Simple markdown to HTML converter for Q&A answers
function renderMarkdown(text) {
  if (!text) return '';
  let html = text
    // Escape HTML
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    // Headers
    .replace(/^### (.+)$/gm, '<h4 style="margin:12px 0 6px;color:#1a237e;font-size:14px">$1</h4>')
    .replace(/^## (.+)$/gm, '<h3 style="margin:14px 0 6px;color:#1a237e;font-size:15px">$1</h3>')
    .replace(/^# (.+)$/gm, '<h2 style="margin:16px 0 8px;color:#1a237e;font-size:16px">$1</h2>')
    // Bold
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    // Italic
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    // Unordered list items
    .replace(/^\* (.+)$/gm, '<li style="margin:2px 0;margin-left:16px;list-style:disc">$1</li>')
    .replace(/^- (.+)$/gm, '<li style="margin:2px 0;margin-left:16px;list-style:disc">$1</li>')
    // Ordered list items
    .replace(/^\d+\. (.+)$/gm, '<li style="margin:2px 0;margin-left:16px;list-style:decimal">$1</li>')
    // Paragraphs (double newline)
    .replace(/\n\n/g, '</p><p style="margin:8px 0">')
    // Single newlines within paragraphs
    .replace(/\n/g, '<br/>');
  return '<p style="margin:8px 0">' + html + '</p>';
}

// ==================== Progress Tracker ====================

function JobProgress({ job, onComplete }) {
  const steps = job.steps || [];
  const completedOrSkipped = steps.filter(s => s.status === 'completed' || s.status === 'skipped').length;
  const progress = steps.length > 0 ? Math.round((completedOrSkipped / steps.length) * 100) : 0;

  return (
    <div className="job-progress-container" style={{
      background: '#f8f9fa', borderRadius: '12px', padding: '28px',
      border: '1px solid #e0e0e0', marginTop: '20px'
    }}>
      <h3 style={{ margin: '0 0 4px 0', color: '#1a237e', wordBreak: 'break-word' }}>Analyzing: {job.fileName}</h3>

      {/* Progress bar with percentage */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
        <div style={{
          flex: 1, height: '8px', backgroundColor: '#e0e0e0',
          borderRadius: '4px', overflow: 'hidden'
        }}>
          <div style={{
            width: `${progress}%`, height: '100%',
            backgroundColor: job.status === 'failed' ? '#d32f2f' : '#1565c0',
            borderRadius: '4px', transition: 'width 0.5s ease'
          }} />
        </div>
        <span style={{ fontSize: '14px', color: '#666', fontWeight: '600', whiteSpace: 'nowrap' }}>{progress}%</span>
      </div>

      {/* Step list */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        {steps.map((step, idx) => (
          <StepIndicator key={step.name} step={step} />
        ))}
      </div>

      {job.status === 'failed' && job.errorMessage && (
        <div style={{
          marginTop: '16px', padding: '12px', backgroundColor: '#fce4ec',
          borderRadius: '8px', color: '#c62828', fontSize: '14px'
        }}>
          Error: {job.errorMessage}
        </div>
      )}
    </div>
  );
}

function StepIndicator({ step }) {
  const icons = {
    completed: { symbol: '\u2713', color: '#2e7d32', bg: '#e8f5e9' },
    in_progress: { symbol: '\u25CF', color: '#1565c0', bg: '#e3f2fd' },
    pending: { symbol: '\u25CB', color: '#9e9e9e', bg: '#f5f5f5' },
    failed: { symbol: '\u2717', color: '#c62828', bg: '#fce4ec' },
    skipped: { symbol: '\u2014', color: '#9e9e9e', bg: '#f5f5f5' }
  };
  const icon = icons[step.status] || icons.pending;

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: '12px',
      padding: '8px 12px', borderRadius: '8px',
      backgroundColor: step.status === 'in_progress' ? icon.bg : 'transparent',
      transition: 'background-color 0.3s'
    }}>
      <span style={{
        width: '24px', height: '24px', borderRadius: '50%',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        backgroundColor: icon.bg, color: icon.color, fontWeight: 'bold', fontSize: '14px',
        animation: step.status === 'in_progress' ? 'pulse 1.5s infinite' : 'none'
      }}>
        {icon.symbol}
      </span>
      <span style={{
        color: step.status === 'in_progress' ? '#1565c0' :
               step.status === 'completed' ? '#2e7d32' :
               step.status === 'failed' ? '#c62828' : '#757575',
        fontWeight: step.status === 'in_progress' ? '600' : '400',
        fontSize: '14px'
      }}>
        {step.label}
        {step.status === 'skipped' && <span style={{ color: '#9e9e9e', fontStyle: 'italic' }}> (skipped)</span>}
      </span>
    </div>
  );
}

// ==================== Main Component ====================

function ContractAnalysis() {
  const navigate = useNavigate();
  const { uuid: urlUuid } = useParams();
  const [file, setFile] = useState(null);
  const [error, setError] = useState(null);
  const [activeJob, setActiveJob] = useState(null);
  const [completedAnalysis, setCompletedAnalysis] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [folders, setFolders] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState(null); // null = All
  const [folderModalOpen, setFolderModalOpen] = useState(false);
  const [folderModalMode, setFolderModalMode] = useState('create'); // 'create' | 'rename'
  const [folderModalName, setFolderModalName] = useState('');
  const [folderModalTarget, setFolderModalTarget] = useState(null); // folder uuid for rename
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [expandedListView, setExpandedListView] = useState(false);
  const [comparisonView, setComparisonView] = useState(null); // comparison data
  const [comparisonLoading, setComparisonLoading] = useState(false);
  const [compareFile, setCompareFile] = useState(null);
  const [compareOriginalFile, setCompareOriginalFile] = useState(null);
  const pollRef = useRef(null);
  const comparePollRef = useRef(null);

  const fetchFolders = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/contract-folders`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setFolders(data.folders || []);
      }
    } catch (err) {
      console.error('Error fetching folders:', err);
    }
  }, []);

  const fetchJobs = useCallback(async () => {
    setLoadingHistory(true);
    try {
      const response = await fetch(`${API_URL}/api/contract-jobs?limit=50`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setJobs((data.jobs || []).map(j => ({
          ...j,
          steps: typeof j.steps === 'string' ? JSON.parse(j.steps) : j.steps,
          analysis_data: typeof j.analysis_data === 'string' ? JSON.parse(j.analysis_data) : j.analysis_data
        })));
      }
    } catch (err) {
      console.error('Error fetching jobs:', err);
    } finally {
      setLoadingHistory(false);
    }
  }, []);

  useEffect(() => {
    fetchJobs();
    fetchFolders();
  }, [fetchJobs, fetchFolders]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      if (comparePollRef.current) clearInterval(comparePollRef.current);
    };
  }, []);

  const startPolling = useCallback((uuid) => {
    if (pollRef.current) clearInterval(pollRef.current);

    const poll = async () => {
      try {
        const response = await fetch(`${API_URL}/api/contract-jobs/${uuid}`, { credentials: 'include' });
        if (!response.ok) return;
        const data = await response.json();
        if (!data.success) return;

        const jobData = {
          uuid: data.uuid,
          status: data.status,
          fileName: data.fileName,
          currentStep: data.currentStep,
          steps: data.steps || [],
          progress: data.progress,
          textLength: data.textLength,
          analysisData: data.analysisData,
          errorMessage: data.errorMessage,
          model: data.model,
          createdAt: data.createdAt,
          updatedAt: data.updatedAt
        };

        setActiveJob(jobData);

        if (data.status === 'completed') {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setCompletedAnalysis({
            uuid: data.uuid,
            analysisData: data.analysisData,
            fileName: data.fileName,
            model: data.model,
            textLength: data.textLength
          });
          setActiveJob(null);
          fetchJobs();
        } else if (data.status === 'failed') {
          clearInterval(pollRef.current);
          pollRef.current = null;
          fetchJobs();
        }
      } catch (err) {
        console.error('Polling error:', err);
      }
    };

    // Poll immediately, then every 2s
    poll();
    pollRef.current = setInterval(poll, 2000);
  }, [fetchJobs]);

  // Deep-link: load job from URL :uuid once history has loaded.
  useEffect(() => {
    if (!urlUuid || !jobs.length) return;
    if (selectedJob?.uuid === urlUuid) return;
    if (activeJob?.uuid === urlUuid) return;
    if (completedAnalysis?.uuid === urlUuid) return;
    const job = jobs.find(j => j.uuid === urlUuid);
    if (!job) return;
    if (job.status === 'completed') {
      setSelectedJob(job);
    } else if (job.status === 'failed') {
      setActiveJob({
        uuid: job.uuid, status: 'failed', fileName: job.file_name,
        steps: job.steps, progress: 0, errorMessage: job.error_message
      });
    } else {
      setActiveJob({
        uuid: job.uuid, status: job.status, fileName: job.file_name,
        steps: job.steps, progress: 0
      });
      startPolling(job.uuid);
    }
  }, [urlUuid, jobs, selectedJob, activeJob, completedAnalysis, startPolling]);

  const handleCreateFolder = async () => {
    if (!folderModalName.trim()) return;
    try {
      const response = await fetch(`${API_URL}/api/contract-folders`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ name: folderModalName.trim() })
      });
      if (response.ok) {
        setFolderModalOpen(false);
        setFolderModalName('');
        fetchFolders();
      }
    } catch (err) {
      alert('Error creating folder: ' + err.message);
    }
  };

  const handleRenameFolder = async () => {
    if (!folderModalName.trim() || !folderModalTarget) return;
    try {
      const response = await fetch(`${API_URL}/api/contract-folders/${folderModalTarget}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ name: folderModalName.trim() })
      });
      if (response.ok) {
        setFolderModalOpen(false);
        setFolderModalName('');
        setFolderModalTarget(null);
        fetchFolders();
      }
    } catch (err) {
      alert('Error renaming folder: ' + err.message);
    }
  };

  const handleDeleteFolder = async (uuid) => {
    if (!window.confirm('Delete this folder? Analyses will be moved to Unfiled.')) return;
    try {
      const response = await fetch(`${API_URL}/api/contract-folders/${uuid}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      if (response.ok) {
        if (selectedFolder === uuid) setSelectedFolder(null);
        fetchFolders();
        fetchJobs();
      }
    } catch (err) {
      alert('Error deleting folder: ' + err.message);
    }
  };

  const handleMoveToFolder = async (jobUuid, folderUuid) => {
    try {
      const response = await fetch(`${API_URL}/api/contract-jobs/${jobUuid}/folder`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ folderUuid })
      });
      if (response.ok) {
        fetchJobs();
        fetchFolders();
      }
    } catch (err) {
      alert('Error moving analysis: ' + err.message);
    }
  };

  const openCreateFolder = () => {
    setFolderModalMode('create');
    setFolderModalName('');
    setFolderModalTarget(null);
    setFolderModalOpen(true);
  };

  const openRenameFolder = (folder) => {
    setFolderModalMode('rename');
    setFolderModalName(folder.name);
    setFolderModalTarget(folder.uuid);
    setFolderModalOpen(true);
  };

  // Filter jobs by selected folder
  const filteredJobs = selectedFolder === null
    ? jobs
    : selectedFolder === 'unfiled'
    ? jobs.filter(j => !j.folder_id)
    : jobs.filter(j => {
        const folder = folders.find(f => f.uuid === selectedFolder);
        return folder && j.folder_id === folder.id;
      });

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    setFile(selectedFile);
    setError(null);
    setCompletedAnalysis(null);
    setActiveJob(null);
  };

  const handleAnalyze = async () => {
    if (!file) {
      setError('Please select a file to analyze');
      return;
    }

    setError(null);
    setCompletedAnalysis(null);
    setSelectedJob(null);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await fetch(`${API_URL}/api/analyze/contract`, {
        method: 'POST',
        body: formData,
        credentials: 'include'
      });

      const data = await response.json();

      if (data.success && data.uuid) {
        // Start polling for job status
        setActiveJob({
          uuid: data.uuid,
          status: 'processing',
          fileName: file.name,
          steps: [],
          progress: 0
        });
        startPolling(data.uuid);
        navigate(`/contract-analysis/${data.uuid}`);
      } else {
        setError(data.error || 'Failed to start analysis');
      }
    } catch (err) {
      setError(`Error: ${err.message}`);
    }
  };

  const handleViewJob = (job) => {
    setSelectedJob(job);
    setCompletedAnalysis(null);
    setActiveJob(null);
    navigate(`/contract-analysis/${job.uuid}`);
  };

  const handleReprocessJob = async (uuid) => {
    try {
      const response = await fetch(`${API_URL}/api/contract-jobs/${uuid}/reprocess`, {
        method: 'POST',
        credentials: 'include'
      });
      const data = await response.json();
      if (!data.success) {
        alert(data.error || 'Failed to reprocess analysis');
        return;
      }
      setSelectedJob(null);
      setCompletedAnalysis(null);
      setActiveJob({ uuid, status: 'processing', fileName: '', steps: [], progress: 0 });
      startPolling(uuid);
      navigate(`/contract-analysis/${uuid}`);
    } catch (err) {
      alert('Error: ' + err.message);
    }
  };

  const handleDeleteJob = async (uuid) => {
    if (!window.confirm('Are you sure you want to delete this analysis?')) return;
    try {
      const response = await fetch(`${API_URL}/api/contract-jobs/${uuid}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      if (response.ok) {
        fetchJobs();
        if (selectedJob && selectedJob.uuid === uuid) setSelectedJob(null);
        if (completedAnalysis && completedAnalysis.uuid === uuid) setCompletedAnalysis(null);
        if (activeJob && activeJob.uuid === uuid) setActiveJob(null);
        if (urlUuid === uuid) navigate('/contract-analysis');
      } else {
        alert('Failed to delete analysis');
      }
    } catch (err) {
      alert('Error: ' + err.message);
    }
  };

  const clearSelection = () => {
    setSelectedJob(null);
    setCompletedAnalysis(null);
    setActiveJob(null);
    setComparisonView(null);
    setCompareFile(null);
    setCompareOriginalFile(null);
    navigate('/contract-analysis');
  };

  const handleCompareUpload = async (jobUuid) => {
    if (!compareFile) return;
    setComparisonLoading(true);
    setComparisonView(null);

    const formData = new FormData();
    formData.append('file', compareFile);
    if (compareOriginalFile) {
      formData.append('originalFile', compareOriginalFile);
    }

    try {
      const response = await fetch(`${API_URL}/api/contract-jobs/${jobUuid}/compare`, {
        method: 'POST',
        body: formData,
        credentials: 'include'
      });
      const data = await response.json();

      if (data.needsOriginal) {
        setComparisonLoading(false);
        setError('Please also upload the original contract file (left input) to enable comparison.');
        return;
      }

      if (data.success) {
        setCompareFile(null);
        setCompareOriginalFile(null);
        // Poll for comparison completion
        const pollComparison = () => {
          const interval = setInterval(async () => {
            try {
              const res = await fetch(`${API_URL}/api/contract-comparisons/${data.comparisonUuid}`, { credentials: 'include' });
              const cData = await res.json();
              if (cData.success && cData.comparison) {
                if (cData.comparison.status === 'completed') {
                  clearInterval(interval);
                  comparePollRef.current = null;
                  setComparisonView(cData.comparison);
                  setComparisonLoading(false);
                  fetchJobs();
                } else if (cData.comparison.status === 'failed') {
                  clearInterval(interval);
                  comparePollRef.current = null;
                  setComparisonLoading(false);
                  setError(cData.comparison.error_message || 'Comparison failed');
                }
              }
            } catch (err) {
              console.error('Comparison poll error:', err);
            }
          }, 3000);
          comparePollRef.current = interval;
          setTimeout(() => {
            if (comparePollRef.current === interval) {
              clearInterval(interval);
              setComparisonLoading(false);
              setError('Comparison timed out');
            }
          }, 600000);
        };
        pollComparison();
      } else {
        setError(data.error || 'Failed to start comparison');
        setComparisonLoading(false);
      }
    } catch (err) {
      setError('Comparison error: ' + err.message);
      setComparisonLoading(false);
    }
  };

  // Determine what analysis data to show
  const displayAnalysis = completedAnalysis
    ? completedAnalysis.analysisData
    : selectedJob?.analysis_data;

  const displayJobUuid = completedAnalysis?.uuid || selectedJob?.uuid || null;

  const displayMeta = completedAnalysis
    ? { fileName: completedAnalysis.fileName, model: completedAnalysis.model }
    : selectedJob
    ? { fileName: selectedJob.file_name, model: selectedJob.model, date: selectedJob.created_at }
    : null;

  const selectedJobUuid = selectedJob?.uuid || completedAnalysis?.uuid || null;

  return (
    <div className="App">
      <header className="App-header" style={{ padding: '1rem 2rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h1 style={{ margin: 0, fontSize: '1.3rem' }}>AI-Powered Legal Contract Analysis</h1>
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="sidebar-toggle-btn"
          style={{
            background: 'rgba(255,255,255,0.2)', border: 'none', color: 'white',
            padding: '6px 12px', borderRadius: '6px', cursor: 'pointer', fontSize: '14px'
          }}
        >
          {sidebarOpen ? '\u2630' : '\u2630'} Contracts
        </button>
      </header>

      <div style={{ display: 'flex', flex: 1, overflow: 'hidden', height: 'calc(100vh - 60px)' }}>
        {/* ===== SIDEBAR ===== */}
        <aside className="contract-sidebar" style={{
          width: sidebarOpen ? '320px' : '0',
          minWidth: sidebarOpen ? '320px' : '0',
          borderRight: sidebarOpen ? '1px solid #e0e0e0' : 'none',
          background: '#fafbfc',
          display: 'flex', flexDirection: 'column',
          overflow: 'hidden',
          transition: 'width 0.25s ease, min-width 0.25s ease'
        }}>
          {sidebarOpen && (
            <>
              {/* Upload section */}
              <div style={{
                padding: '16px', borderBottom: '1px solid #e0e0e0'
              }}>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <label htmlFor="file-input-sidebar" style={{
                    flex: 1, padding: '8px 12px', background: '#fff',
                    border: '1px dashed #bbb', borderRadius: '8px',
                    cursor: 'pointer', fontSize: '12px', color: '#666',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    textAlign: 'center'
                  }}>
                    {file ? file.name : 'Choose File...'}
                  </label>
                  <input
                    type="file"
                    accept=".pdf,.docx,.txt"
                    onChange={handleFileChange}
                    id="file-input-sidebar"
                    style={{ display: 'none' }}
                  />
                  <button
                    onClick={handleAnalyze}
                    disabled={!file || !!activeJob}
                    style={{
                      padding: '8px 14px', borderRadius: '8px', border: 'none',
                      background: (!file || !!activeJob) ? '#ccc' : '#1565c0',
                      color: 'white', cursor: (!file || !!activeJob) ? 'default' : 'pointer',
                      fontSize: '12px', fontWeight: '600', whiteSpace: 'nowrap'
                    }}
                  >
                    {activeJob ? 'Analyzing...' : 'Analyze'}
                  </button>
                </div>
                {error && (
                  <div style={{
                    marginTop: '8px', padding: '6px 10px', background: '#fce4ec',
                    borderRadius: '6px', color: '#c62828', fontSize: '11px'
                  }}>{error}</div>
                )}
              </div>

              {/* Folder nav */}
              <div style={{ padding: '12px 0', borderBottom: '1px solid #e0e0e0' }}>
                <SidebarFolderItem
                  label="All Contracts"
                  count={jobs.length}
                  active={selectedFolder === null}
                  onClick={() => setSelectedFolder(null)}
                />
                <SidebarFolderItem
                  label="Unfiled"
                  count={jobs.filter(j => !j.folder_id).length}
                  active={selectedFolder === 'unfiled'}
                  onClick={() => setSelectedFolder('unfiled')}
                />
                {folders.map(f => (
                  <SidebarFolderItem
                    key={f.uuid}
                    label={f.name}
                    count={f.job_count}
                    active={selectedFolder === f.uuid}
                    onClick={() => setSelectedFolder(f.uuid)}
                    onRename={() => openRenameFolder(f)}
                    onDelete={() => handleDeleteFolder(f.uuid)}
                  />
                ))}
                <div style={{ padding: '4px 16px', display: 'flex', gap: '8px' }}>
                  <button onClick={openCreateFolder} style={{
                    background: 'none', border: 'none', color: '#1565c0',
                    cursor: 'pointer', fontSize: '12px', fontWeight: '600', padding: '4px 0'
                  }}>+ New Folder</button>
                  <span style={{ color: '#ddd' }}>|</span>
                  <button onClick={() => setExpandedListView(!expandedListView)} style={{
                    background: 'none', border: 'none', color: '#666',
                    cursor: 'pointer', fontSize: '12px', padding: '4px 0'
                  }}>{expandedListView ? 'Close List' : 'Expand List'}</button>
                </div>
              </div>

              {/* Job list */}
              <div style={{ flex: 1, overflowY: 'auto', padding: '8px 0' }}>
                {loadingHistory ? (
                  <p style={{ padding: '16px', color: '#999', fontSize: '13px', textAlign: 'center' }}>Loading...</p>
                ) : filteredJobs.length === 0 ? (
                  <p style={{ padding: '16px', color: '#999', fontSize: '13px', textAlign: 'center', fontStyle: 'italic' }}>
                    {jobs.length === 0 ? 'No analyses yet' : 'No analyses in this folder'}
                  </p>
                ) : (
                  filteredJobs.map(job => {
                    const isSelected = selectedJobUuid === job.uuid;
                    const statusColor = job.status === 'completed' ? '#2e7d32' : job.status === 'processing' ? '#1565c0' : '#c62828';
                    const folder = folders.find(f => f.id === job.folder_id);
                    return (
                      <SidebarJobItem
                        key={job.uuid}
                        job={job}
                        isSelected={isSelected}
                        statusColor={statusColor}
                        folder={folder}
                        onSelect={() => {
                          if (job.status === 'completed') {
                            handleViewJob(job);
                            setExpandedListView(false);
                          } else if (job.status === 'processing') {
                            setActiveJob(null);
                            startPolling(job.uuid);
                            setActiveJob({ uuid: job.uuid, status: 'processing', fileName: job.file_name, steps: job.steps, progress: 0 });
                            setExpandedListView(false);
                            navigate(`/contract-analysis/${job.uuid}`);
                          }
                        }}
                        onDelete={() => handleDeleteJob(job.uuid)}
                        folders={folders}
                        onMoveToFolder={(folderUuid) => handleMoveToFolder(job.uuid, folderUuid)}
                      />
                    );
                  })
                )}
              </div>
            </>
          )}
        </aside>

        {/* ===== MAIN CONTENT ===== */}
        <main style={{ flex: 1, overflowY: 'auto', background: '#f5f6f8' }}>
          {expandedListView ? (
            /* Expanded list view */
            <div style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                <h2 style={{ margin: 0, color: '#1a237e' }}>All Contracts</h2>
                <button
                  onClick={() => setExpandedListView(false)}
                  style={{
                    padding: '6px 14px', borderRadius: '8px', border: '1px solid #ccc',
                    background: 'white', cursor: 'pointer', fontSize: '13px'
                  }}
                >Back to Analysis</button>
              </div>

              {/* Unfiled section */}
              <ExpandedFolderSection
                title="Unfiled"
                jobs={jobs.filter(j => !j.folder_id)}
                folders={folders}
                selectedJobUuid={selectedJobUuid}
                onSelectJob={(job) => { handleViewJob(job); setExpandedListView(false); }}
                onDeleteJob={handleDeleteJob}
                onMoveToFolder={handleMoveToFolder}
                onViewProgress={(job) => {
                  setActiveJob(null);
                  startPolling(job.uuid);
                  setActiveJob({ uuid: job.uuid, status: 'processing', fileName: job.file_name, steps: job.steps, progress: 0 });
                  setExpandedListView(false);
                  navigate(`/contract-analysis/${job.uuid}`);
                }}
              />

              {/* Folder sections */}
              {folders.map(f => (
                <ExpandedFolderSection
                  key={f.uuid}
                  title={f.name}
                  jobs={jobs.filter(j => j.folder_id === f.id)}
                  folders={folders}
                  selectedJobUuid={selectedJobUuid}
                  onSelectJob={(job) => { handleViewJob(job); setExpandedListView(false); }}
                  onDeleteJob={handleDeleteJob}
                  onMoveToFolder={handleMoveToFolder}
                  onRename={() => openRenameFolder(f)}
                  onDeleteFolder={() => handleDeleteFolder(f.uuid)}
                  onViewProgress={(job) => {
                    setActiveJob(null);
                    startPolling(job.uuid);
                    setActiveJob({ uuid: job.uuid, status: 'processing', fileName: job.file_name, steps: job.steps, progress: 0 });
                    setExpandedListView(false);
                    navigate(`/contract-analysis/${job.uuid}`);
                  }}
                />
              ))}
            </div>
          ) : activeJob ? (
            /* Active job progress */
            <div style={{ padding: '24px', maxWidth: '900px', margin: '0 auto' }}>
              <JobProgress job={activeJob} />
            </div>
          ) : displayAnalysis ? (
            /* Analysis results */
            <div style={{ padding: '24px', maxWidth: '1100px', margin: '0 auto' }}>
              {displayMeta && (
                <div style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  marginBottom: '20px', padding: '14px 20px', background: 'white',
                  borderRadius: '10px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)'
                }}>
                  <div style={{ display: 'flex', gap: '16px', alignItems: 'center', flexWrap: 'wrap' }}>
                    <h2 style={{ margin: 0, fontSize: '16px', color: '#1a237e' }}>{displayMeta.fileName}</h2>
                    {displayMeta.model && <span style={{ fontSize: '12px', color: '#888' }}>{displayMeta.model}</span>}
                    {displayMeta.date && <span style={{ fontSize: '12px', color: '#888' }}>{new Date(displayMeta.date).toLocaleDateString()}</span>}
                  </div>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    {selectedJobUuid && (
                      <button onClick={() => handleReprocessJob(selectedJobUuid)} style={{
                        padding: '6px 14px', borderRadius: '8px', border: '1px solid #90caf9',
                        background: 'white', cursor: 'pointer', fontSize: '12px', color: '#1565c0'
                      }}>Reprocess</button>
                    )}
                    {selectedJobUuid && (
                      <button onClick={() => handleDeleteJob(selectedJobUuid)} style={{
                        padding: '6px 14px', borderRadius: '8px', border: '1px solid #ef9a9a',
                        background: 'white', cursor: 'pointer', fontSize: '12px', color: '#c62828'
                      }}>Delete</button>
                    )}
                    {selectedJob && (
                      <button onClick={clearSelection} style={{
                        padding: '6px 14px', borderRadius: '8px', border: '1px solid #ccc',
                        background: 'white', cursor: 'pointer', fontSize: '12px'
                      }}>Clear</button>
                    )}
                  </div>
                </div>
              )}

              {/* Compare Versions Section */}
              {selectedJobUuid && !comparisonView && (
                <div style={{
                  marginBottom: '20px', padding: '16px 20px', background: 'white',
                  borderRadius: '10px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)'
                }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                      <span style={{ fontSize: '14px', fontWeight: '600', color: '#1a237e' }}>Compare Versions</span>
                      <label htmlFor="compare-orig-input" style={{
                        flex: 1, minWidth: '120px', maxWidth: '250px', padding: '8px 12px',
                        background: '#f5f6f8', border: '1px dashed #bbb', borderRadius: '8px',
                        cursor: 'pointer', fontSize: '12px', color: '#666',
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textAlign: 'center'
                      }}>
                        {compareOriginalFile ? compareOriginalFile.name : 'Original (optional)'}
                      </label>
                      <input
                        type="file" accept=".pdf,.docx,.txt" id="compare-orig-input"
                        onChange={e => setCompareOriginalFile(e.target.files[0])}
                        style={{ display: 'none' }}
                      />
                      <label htmlFor="compare-file-input" style={{
                        flex: 1, minWidth: '120px', maxWidth: '250px', padding: '8px 12px',
                        background: '#f0ecff', border: '1px dashed #7c3aed', borderRadius: '8px',
                        cursor: 'pointer', fontSize: '12px', color: '#5b21b6',
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textAlign: 'center'
                      }}>
                        {compareFile ? compareFile.name : 'New version *'}
                      </label>
                      <input
                        type="file" accept=".pdf,.docx,.txt" id="compare-file-input"
                        onChange={e => setCompareFile(e.target.files[0])}
                        style={{ display: 'none' }}
                      />
                      <button
                        onClick={() => handleCompareUpload(selectedJobUuid)}
                        disabled={!compareFile || comparisonLoading}
                        style={{
                          padding: '8px 16px', borderRadius: '8px', border: 'none',
                          background: (!compareFile || comparisonLoading) ? '#ccc' : '#7c3aed',
                          color: 'white', cursor: (!compareFile || comparisonLoading) ? 'default' : 'pointer',
                          fontSize: '12px', fontWeight: '600', whiteSpace: 'nowrap'
                        }}
                      >
                        {comparisonLoading ? 'Comparing...' : 'Compare'}
                      </button>
                    </div>
                    <div style={{ fontSize: '11px', color: '#999' }}>
                      Upload original for line-by-line text diff. Only the new version is required.
                    </div>
                  </div>
                  {comparisonLoading && (
                    <div style={{ marginTop: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <div style={{ width: '16px', height: '16px', border: '2px solid #7c3aed', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                      <span style={{ fontSize: '13px', color: '#666' }}>Analyzing new version and generating comparison...</span>
                    </div>
                  )}
                </div>
              )}

              {/* Comparison Results */}
              {comparisonView && (
                <ContractComparisonView
                  comparison={comparisonView}
                  onClose={() => setComparisonView(null)}
                />
              )}

              <ContractAnalysisView data={displayAnalysis} jobUuid={displayJobUuid} />
            </div>
          ) : (
            /* Empty state */
            <div style={{
              display: 'flex', flexDirection: 'column', alignItems: 'center',
              justifyContent: 'center', height: '100%', color: '#999', padding: '40px'
            }}>
              <div style={{ fontSize: '48px', marginBottom: '16px', opacity: 0.3 }}>&#128196;</div>
              <h3 style={{ margin: '0 0 8px 0', color: '#666', fontWeight: '500' }}>
                {jobs.length === 0 ? 'Upload a contract to get started' : 'Select a contract to view its analysis'}
              </h3>
              <p style={{ fontSize: '14px', color: '#aaa', margin: 0 }}>
                {jobs.length === 0
                  ? 'Use the sidebar to upload a PDF, DOCX, or TXT file'
                  : 'Click on any contract in the sidebar to view the results'}
              </p>
            </div>
          )}
        </main>
      </div>

      {/* Folder create/rename modal */}
      {folderModalOpen && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex',
          alignItems: 'center', justifyContent: 'center', zIndex: 1000
        }} onClick={() => setFolderModalOpen(false)}>
          <div style={{
            background: 'white', borderRadius: '12px', padding: '24px',
            minWidth: '320px', maxWidth: '90%'
          }} onClick={e => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 16px 0' }}>
              {folderModalMode === 'create' ? 'Create Folder' : 'Rename Folder'}
            </h3>
            <input
              type="text"
              value={folderModalName}
              onChange={e => setFolderModalName(e.target.value)}
              placeholder="Folder name"
              autoFocus
              onKeyDown={e => e.key === 'Enter' && (folderModalMode === 'create' ? handleCreateFolder() : handleRenameFolder())}
              style={{
                width: '100%', padding: '10px 12px', borderRadius: '8px',
                border: '1px solid #ccc', fontSize: '14px', boxSizing: 'border-box'
              }}
            />
            <div style={{ display: 'flex', gap: '8px', marginTop: '16px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setFolderModalOpen(false)}
                style={{
                  padding: '8px 16px', borderRadius: '8px', border: '1px solid #ccc',
                  background: 'white', cursor: 'pointer'
                }}
              >Cancel</button>
              <button
                onClick={folderModalMode === 'create' ? handleCreateFolder : handleRenameFolder}
                style={{
                  padding: '8px 16px', borderRadius: '8px', border: 'none',
                  background: '#1565c0', color: 'white', cursor: 'pointer', fontWeight: '600'
                }}
              >{folderModalMode === 'create' ? 'Create' : 'Rename'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div className="sidebar-overlay" onClick={() => setSidebarOpen(false)} />
      )}

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
        .sidebar-overlay {
          display: none;
        }
        @media (max-width: 768px) {
          .contract-sidebar {
            position: fixed !important;
            top: 60px !important;
            left: 0 !important;
            bottom: 0 !important;
            z-index: 500 !important;
            box-shadow: 4px 0 16px rgba(0,0,0,0.15) !important;
          }
          .sidebar-overlay {
            display: block;
            position: fixed;
            top: 60px;
            left: 320px;
            right: 0;
            bottom: 0;
            background: rgba(0,0,0,0.3);
            z-index: 499;
          }
        }
      `}</style>
    </div>
  );
}

// ==================== Contract Comparison View ====================

function ContractComparisonView({ comparison, onClose }) {
  const [activeTab, setActiveTab] = useState('synopsis');
  const [expandedDiff, setExpandedDiff] = useState({});

  const diffData = typeof comparison.diff_data === 'string'
    ? JSON.parse(comparison.diff_data)
    : comparison.diff_data;

  const textDiff = diffData?.textDiff || [];
  const changes = diffData?.changes || [];
  const riskChanges = diffData?.risk_changes || '';
  const overallAssessment = diffData?.overall_assessment || '';

  const significanceColor = (sig) => {
    const s = (sig || '').toLowerCase();
    if (s === 'high' || s === 'critical') return { bg: '#fce4ec', color: '#c62828' };
    if (s === 'medium' || s === 'moderate') return { bg: '#fff3e0', color: '#e65100' };
    return { bg: '#e8f5e9', color: '#2e7d32' };
  };

  const hasDiff = textDiff.length > 0;
  const tabs = [
    { key: 'synopsis', label: 'Synopsis' },
    { key: 'changes', label: `Changes (${changes.length})` },
    ...(hasDiff ? [{ key: 'diff', label: 'Text Diff' }] : []),
  ];

  const addedCount = textDiff.filter(d => d.type === 'added').length;
  const removedCount = textDiff.filter(d => d.type === 'removed').length;

  return (
    <div style={{
      marginBottom: '20px', background: 'white', borderRadius: '12px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.08)', border: '1px solid #e0e0e0', overflow: 'hidden'
    }}>
      {/* Header */}
      <div style={{
        padding: '16px 20px', background: 'linear-gradient(135deg, #7c3aed 0%, #5b21b6 100%)',
        color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center'
      }}>
        <div>
          <h3 style={{ margin: '0 0 4px 0', fontSize: '16px' }}>Version Comparison</h3>
          <div style={{ fontSize: '12px', opacity: 0.85 }}>
            {comparison.original_file_name} → {comparison.new_file_name}
          </div>
        </div>
        <button onClick={onClose} style={{
          background: 'rgba(255,255,255,0.2)', border: 'none', color: 'white',
          borderRadius: '8px', padding: '6px 14px', cursor: 'pointer', fontSize: '13px'
        }}>Close</button>
      </div>

      {/* Tabs */}
      <div style={{
        display: 'flex', borderBottom: '1px solid #e0e0e0', background: '#fafafa'
      }}>
        {tabs.map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            style={{
              padding: '10px 20px', border: 'none', borderBottom: activeTab === tab.key ? '2px solid #7c3aed' : '2px solid transparent',
              background: 'none', cursor: 'pointer', fontSize: '13px', fontWeight: activeTab === tab.key ? '600' : '400',
              color: activeTab === tab.key ? '#7c3aed' : '#666'
            }}
          >{tab.label}</button>
        ))}
      </div>

      {/* Tab Content */}
      <div style={{ padding: '20px' }}>
        {/* Synopsis Tab */}
        {activeTab === 'synopsis' && (
          <div>
            <div style={{
              padding: '16px', background: '#f5f3ff', borderRadius: '10px',
              border: '1px solid #ede9fe', marginBottom: '16px'
            }}>
              <h4 style={{ margin: '0 0 8px 0', color: '#5b21b6', fontSize: '14px' }}>Summary of Changes</h4>
              <p style={{ margin: 0, fontSize: '14px', lineHeight: '1.6', color: '#333' }}>
                {comparison.synopsis}
              </p>
            </div>

            {overallAssessment && (
              <div style={{
                padding: '16px', background: '#f8f9fa', borderRadius: '10px',
                border: '1px solid #e0e0e0', marginBottom: '16px'
              }}>
                <h4 style={{ margin: '0 0 8px 0', color: '#1a237e', fontSize: '14px' }}>Overall Assessment</h4>
                <p style={{ margin: 0, fontSize: '14px', lineHeight: '1.6', color: '#333' }}>
                  {overallAssessment}
                </p>
              </div>
            )}

            {riskChanges && (
              <div style={{
                padding: '16px', background: '#fff8e1', borderRadius: '10px',
                border: '1px solid #fff0b3', marginBottom: '16px'
              }}>
                <h4 style={{ margin: '0 0 8px 0', color: '#e65100', fontSize: '14px' }}>Risk Changes</h4>
                <p style={{ margin: 0, fontSize: '14px', lineHeight: '1.6', color: '#333' }}>
                  {riskChanges}
                </p>
              </div>
            )}

            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
              {hasDiff && (<>
                <div style={{
                  padding: '10px 16px', background: '#e8f5e9', borderRadius: '8px',
                  fontSize: '13px', color: '#2e7d32', fontWeight: '600'
                }}>+{addedCount} lines added</div>
                <div style={{
                  padding: '10px 16px', background: '#fce4ec', borderRadius: '8px',
                  fontSize: '13px', color: '#c62828', fontWeight: '600'
                }}>-{removedCount} lines removed</div>
              </>)}
              <div style={{
                padding: '10px 16px', background: '#e3f2fd', borderRadius: '8px',
                fontSize: '13px', color: '#1565c0', fontWeight: '600'
              }}>{changes.length} changes identified</div>
            </div>
          </div>
        )}

        {/* Changes Tab */}
        {activeTab === 'changes' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {changes.length === 0 ? (
              <p style={{ color: '#999', fontSize: '14px' }}>No specific changes identified.</p>
            ) : changes.map((change, idx) => {
              const sc = significanceColor(change.significance);
              return (
                <div key={idx} style={{
                  padding: '14px 16px', background: '#f8f9fa', borderRadius: '10px',
                  border: '1px solid #e8e8e8'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', flexWrap: 'wrap' }}>
                    {change.category && (
                      <span style={{
                        padding: '3px 10px', borderRadius: '12px', fontSize: '11px',
                        fontWeight: '600', background: '#e3f2fd', color: '#1565c0'
                      }}>{change.category}</span>
                    )}
                    {change.section && (
                      <span style={{ fontSize: '13px', fontWeight: '600', color: '#333' }}>{change.section}</span>
                    )}
                    {change.significance && (
                      <span style={{
                        padding: '3px 10px', borderRadius: '12px', fontSize: '11px',
                        fontWeight: '600', background: sc.bg, color: sc.color
                      }}>{change.significance}</span>
                    )}
                  </div>
                  {change.description && (
                    <p style={{ margin: '0 0 6px 0', fontSize: '13px', color: '#444', lineHeight: '1.5' }}>
                      {change.description}
                    </p>
                  )}
                  {change.legal_impact && (
                    <p style={{ margin: 0, fontSize: '12px', color: '#666', fontStyle: 'italic' }}>
                      Legal impact: {change.legal_impact}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Text Diff Tab */}
        {activeTab === 'diff' && (
          <div style={{
            fontFamily: 'monospace', fontSize: '12px', lineHeight: '1.6',
            maxHeight: '600px', overflowY: 'auto', border: '1px solid #e0e0e0',
            borderRadius: '8px'
          }}>
            {textDiff.map((entry, idx) => {
              if (entry.type === 'collapsed') {
                const isExpanded = expandedDiff[idx];
                return (
                  <div key={idx} style={{
                    padding: '6px 16px', background: '#f0f0f0', color: '#888',
                    textAlign: 'center', cursor: 'pointer', fontSize: '11px',
                    borderTop: '1px solid #e0e0e0', borderBottom: '1px solid #e0e0e0'
                  }}
                  onClick={() => setExpandedDiff(prev => ({ ...prev, [idx]: !prev[idx] }))}
                  >
                    {isExpanded ? '▼' : '▶'} {entry.count} unchanged line{entry.count !== 1 ? 's' : ''}
                  </div>
                );
              }

              const colors = {
                added: { bg: '#e6ffec', border: '#abf2bc', prefix: '+', color: '#1a7f37' },
                removed: { bg: '#ffebe9', border: '#ffcecb', prefix: '-', color: '#cf222e' },
                unchanged: { bg: 'white', border: 'transparent', prefix: ' ', color: '#333' }
              };
              const c = colors[entry.type] || colors.unchanged;

              return (
                <div key={idx} style={{
                  padding: '1px 16px', background: c.bg,
                  borderLeft: `3px solid ${c.border}`, color: c.color,
                  whiteSpace: 'pre-wrap', wordBreak: 'break-all'
                }}>
                  <span style={{ color: '#999', display: 'inline-block', width: '40px', textAlign: 'right', marginRight: '12px', userSelect: 'none' }}>
                    {entry.lineNum || ''}
                  </span>
                  <span style={{ color: c.color, marginRight: '8px', fontWeight: '700' }}>{c.prefix}</span>
                  {entry.line}
                </div>
              );
            })}
            {textDiff.length === 0 && (
              <div style={{ padding: '20px', textAlign: 'center', color: '#999' }}>
                No text differences found.
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ==================== Sub-Components ====================

function StatusBadge({ status }) {
  const styles = {
    completed: { bg: '#e8f5e9', color: '#2e7d32', label: 'Completed' },
    processing: { bg: '#e3f2fd', color: '#1565c0', label: 'Processing' },
    failed: { bg: '#fce4ec', color: '#c62828', label: 'Failed' }
  };
  const s = styles[status] || styles.processing;
  return (
    <span style={{
      padding: '4px 10px', borderRadius: '12px', fontSize: '12px',
      fontWeight: '600', backgroundColor: s.bg, color: s.color
    }}>
      {s.label}
    </span>
  );
}

function MiniProgress({ steps }) {
  if (!steps || steps.length === 0) return null;
  const completedOrSkipped = steps.filter(s => s.status === 'completed' || s.status === 'skipped').length;
  const progress = Math.round((completedOrSkipped / steps.length) * 100);
  const current = steps.find(s => s.status === 'in_progress');

  return (
    <div style={{ marginTop: '8px' }}>
      <div style={{ width: '100%', height: '4px', backgroundColor: '#e0e0e0', borderRadius: '2px', overflow: 'hidden' }}>
        <div style={{ width: `${progress}%`, height: '100%', backgroundColor: '#1565c0', borderRadius: '2px', transition: 'width 0.5s ease' }} />
      </div>
      {current && (
        <p style={{ fontSize: '12px', color: '#1565c0', marginTop: '4px' }}>
          {current.label}...
        </p>
      )}
    </div>
  );
}

function MoveToFolderDropdown({ folders, currentFolderId, onMove }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      <button
        onClick={() => setOpen(!open)}
        style={{
          padding: '6px 10px', borderRadius: '6px', border: '1px solid #ccc',
          background: 'white', cursor: 'pointer', fontSize: '12px', color: '#555'
        }}
      >
        Move to...
      </button>
      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, background: 'white',
          border: '1px solid #ddd', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          zIndex: 100, overflow: 'hidden', minWidth: '160px', marginTop: '4px'
        }}>
          {currentFolderId && (
            <button onClick={() => { setOpen(false); onMove(null); }} style={{
              display: 'block', width: '100%', padding: '8px 14px', border: 'none',
              background: 'white', cursor: 'pointer', textAlign: 'left', fontSize: '13px',
              fontStyle: 'italic', color: '#666'
            }}>Remove from folder</button>
          )}
          {folders.map(f => (
            <button
              key={f.uuid}
              onClick={() => { setOpen(false); onMove(f.uuid); }}
              disabled={f.id === currentFolderId}
              style={{
                display: 'block', width: '100%', padding: '8px 14px', border: 'none',
                background: f.id === currentFolderId ? '#f0f0f0' : 'white',
                cursor: f.id === currentFolderId ? 'default' : 'pointer',
                textAlign: 'left', fontSize: '13px',
                color: f.id === currentFolderId ? '#999' : '#333'
              }}
            >{f.name}{f.id === currentFolderId ? ' (current)' : ''}</button>
          ))}
        </div>
      )}
    </div>
  );
}

function SidebarFolderItem({ label, count, active, onClick, onRename, onDelete }) {
  const [menuOpen, setMenuOpen] = useState(false);
  return (
    <div
      onClick={onClick}
      style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '7px 16px', cursor: 'pointer',
        background: active ? '#e3f2fd' : 'transparent',
        borderLeft: active ? '3px solid #1565c0' : '3px solid transparent',
        transition: 'background 0.15s'
      }}
      onMouseEnter={e => { if (!active) e.currentTarget.style.background = '#f0f0f0'; }}
      onMouseLeave={e => { if (!active) e.currentTarget.style.background = 'transparent'; }}
    >
      <span style={{
        fontSize: '13px', color: active ? '#1565c0' : '#333',
        fontWeight: active ? '600' : '400',
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1
      }}>{label}</span>
      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
        <span style={{
          fontSize: '11px', color: '#999', background: active ? '#bbdefb' : '#eee',
          padding: '1px 7px', borderRadius: '10px', fontWeight: '600'
        }}>{count}</span>
        {(onRename || onDelete) && (
          <div style={{ position: 'relative' }}>
            <button
              onClick={e => { e.stopPropagation(); setMenuOpen(!menuOpen); }}
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                color: '#999', fontSize: '12px', padding: '2px 4px'
              }}
            >...</button>
            {menuOpen && (
              <div style={{
                position: 'absolute', top: '100%', right: 0, background: 'white',
                border: '1px solid #ddd', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                zIndex: 100, overflow: 'hidden', minWidth: '100px'
              }}>
                {onRename && (
                  <button onClick={e => { e.stopPropagation(); setMenuOpen(false); onRename(); }} style={{
                    display: 'block', width: '100%', padding: '8px 12px', border: 'none',
                    background: 'white', cursor: 'pointer', textAlign: 'left', fontSize: '12px'
                  }}>Rename</button>
                )}
                {onDelete && (
                  <button onClick={e => { e.stopPropagation(); setMenuOpen(false); onDelete(); }} style={{
                    display: 'block', width: '100%', padding: '8px 12px', border: 'none',
                    background: 'white', cursor: 'pointer', textAlign: 'left', fontSize: '12px', color: '#c62828'
                  }}>Delete</button>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function SidebarJobItem({ job, isSelected, statusColor, folder, onSelect, onDelete, folders, onMoveToFolder }) {
  const [menuOpen, setMenuOpen] = useState(false);
  return (
    <div
      onClick={onSelect}
      style={{
        padding: '10px 16px', cursor: 'pointer',
        background: isSelected ? '#e8eaf6' : 'transparent',
        borderLeft: isSelected ? '3px solid #1a237e' : '3px solid transparent',
        transition: 'background 0.15s'
      }}
      onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = '#f5f5f5'; }}
      onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = 'transparent'; }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1, minWidth: 0 }}>
          <span style={{
            width: '8px', height: '8px', borderRadius: '50%',
            background: statusColor, flexShrink: 0
          }} />
          <span style={{
            fontSize: '13px', fontWeight: isSelected ? '600' : '400',
            color: '#333', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
          }}>{job.file_name}</span>
        </div>
        <div style={{ position: 'relative', flexShrink: 0 }}>
          <button
            onClick={e => { e.stopPropagation(); setMenuOpen(!menuOpen); }}
            style={{
              background: menuOpen ? '#e8eaf6' : 'none', border: 'none', cursor: 'pointer',
              color: menuOpen ? '#1a237e' : '#999', fontSize: '16px', padding: '2px 6px',
              borderRadius: '4px', fontWeight: 'bold', lineHeight: '1'
            }}
            onMouseEnter={e => { e.currentTarget.style.color = '#1a237e'; e.currentTarget.style.background = '#f0f0f5'; }}
            onMouseLeave={e => { if (!menuOpen) { e.currentTarget.style.color = '#999'; e.currentTarget.style.background = 'none'; } }}
          >&#8942;</button>
          {menuOpen && (
            <div style={{
              position: 'absolute', top: '100%', right: 0, background: 'white',
              border: '1px solid #ddd', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
              zIndex: 200, overflow: 'hidden', minWidth: '150px'
            }} onClick={e => e.stopPropagation()}>
              {folders.length > 0 && (
                <>
                  <div style={{ padding: '6px 12px', fontSize: '10px', color: '#999', fontWeight: '600', textTransform: 'uppercase' }}>
                    Move to folder
                  </div>
                  {job.folder_id && (
                    <button onClick={() => { setMenuOpen(false); onMoveToFolder(null); }} style={{
                      display: 'block', width: '100%', padding: '6px 12px', border: 'none',
                      background: 'white', cursor: 'pointer', textAlign: 'left', fontSize: '12px',
                      color: '#666', fontStyle: 'italic'
                    }}>Remove from folder</button>
                  )}
                  {folders.map(f => (
                    <button
                      key={f.uuid}
                      onClick={() => { setMenuOpen(false); onMoveToFolder(f.uuid); }}
                      disabled={f.id === job.folder_id}
                      style={{
                        display: 'block', width: '100%', padding: '6px 12px', border: 'none',
                        background: f.id === job.folder_id ? '#f5f5f5' : 'white',
                        cursor: f.id === job.folder_id ? 'default' : 'pointer',
                        textAlign: 'left', fontSize: '12px',
                        color: f.id === job.folder_id ? '#ccc' : '#333'
                      }}
                    >{f.name}</button>
                  ))}
                  <div style={{ borderTop: '1px solid #eee', margin: '4px 0' }} />
                </>
              )}
              <button onClick={() => { setMenuOpen(false); onDelete(); }} style={{
                display: 'block', width: '100%', padding: '6px 12px', border: 'none',
                background: 'white', cursor: 'pointer', textAlign: 'left', fontSize: '12px', color: '#c62828'
              }}>Delete</button>
            </div>
          )}
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '2px', paddingLeft: '16px' }}>
        <span style={{ fontSize: '11px', color: '#aaa' }}>
          {new Date(job.created_at).toLocaleDateString()}
        </span>
        {folder && (
          <span style={{
            fontSize: '10px', padding: '1px 6px', borderRadius: '8px',
            background: '#e3f2fd', color: '#1565c0', fontWeight: '500'
          }}>{folder.name}</span>
        )}
      </div>
      {job.status === 'processing' && <MiniProgress steps={job.steps} />}
    </div>
  );
}

function ExpandedFolderSection({ title, jobs, folders, selectedJobUuid, onSelectJob, onDeleteJob, onMoveToFolder, onRename, onDeleteFolder, onViewProgress }) {
  const [collapsed, setCollapsed] = useState(false);
  if (jobs.length === 0 && !onRename) return null;

  return (
    <div style={{ marginBottom: '20px' }}>
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '10px 16px', background: '#fff', borderRadius: '8px 8px 0 0',
        border: '1px solid #e0e0e0',
        borderBottom: collapsed ? '1px solid #e0e0e0' : 'none',
        borderRadius: collapsed ? '8px' : '8px 8px 0 0', cursor: 'pointer'
      }} onClick={() => setCollapsed(!collapsed)}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: '12px', color: '#999', transform: collapsed ? 'rotate(-90deg)' : 'rotate(0)', transition: 'transform 0.2s' }}>
            &#9660;
          </span>
          <h3 style={{ margin: 0, fontSize: '15px', color: '#1a237e' }}>{title}</h3>
          <span style={{ fontSize: '12px', color: '#999' }}>({jobs.length})</span>
        </div>
        {(onRename || onDeleteFolder) && (
          <div style={{ display: 'flex', gap: '8px' }} onClick={e => e.stopPropagation()}>
            {onRename && <button onClick={onRename} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '12px', color: '#1565c0' }}>Rename</button>}
            {onDeleteFolder && <button onClick={onDeleteFolder} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '12px', color: '#c62828' }}>Delete</button>}
          </div>
        )}
      </div>
      {!collapsed && jobs.length > 0 && (
        <div style={{ border: '1px solid #e0e0e0', borderTop: 'none', borderRadius: '0 0 8px 8px', overflow: 'hidden' }}>
          {jobs.map(job => (
            <div key={job.uuid} style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '12px 16px', borderBottom: '1px solid #f0f0f0',
              background: selectedJobUuid === job.uuid ? '#e8eaf6' : 'white',
              cursor: 'pointer',
              transition: 'background 0.15s'
            }}
              onClick={() => job.status === 'completed' ? onSelectJob(job) : job.status === 'processing' ? onViewProgress(job) : null}
              onMouseEnter={e => { if (selectedJobUuid !== job.uuid) e.currentTarget.style.background = '#fafafa'; }}
              onMouseLeave={e => { if (selectedJobUuid !== job.uuid) e.currentTarget.style.background = 'white'; }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1, minWidth: 0 }}>
                <StatusBadge status={job.status} />
                <span style={{ fontSize: '14px', fontWeight: '500', color: '#333', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{job.file_name}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexShrink: 0 }}>
                <span style={{ fontSize: '12px', color: '#999' }}>{new Date(job.created_at).toLocaleDateString()}</span>
                <span style={{ fontSize: '12px', color: '#999' }}>{(job.file_size / 1024).toFixed(0)} KB</span>
                {folders.length > 0 && (
                  <MoveToFolderDropdown
                    folders={folders}
                    currentFolderId={job.folder_id}
                    onMove={(folderUuid) => onMoveToFolder(job.uuid, folderUuid)}
                  />
                )}
                <button onClick={e => { e.stopPropagation(); onDeleteJob(job.uuid); }} style={{
                  background: 'none', border: 'none', cursor: 'pointer', color: '#c62828', fontSize: '12px'
                }}>Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}
      {!collapsed && jobs.length === 0 && (
        <div style={{
          border: '1px solid #e0e0e0', borderTop: 'none', borderRadius: '0 0 8px 8px',
          padding: '16px', textAlign: 'center', color: '#aaa', fontSize: '13px', fontStyle: 'italic'
        }}>No contracts in this folder</div>
      )}
    </div>
  );
}

function AskQuestionButton({ sectionKey, contextText, sectionType, jobUuid, questions, onNewQuestion }) {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const chatEndRef = useRef(null);
  const sectionQs = (questions || []).filter(q => q.section_key === sectionKey);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open]);

  // Auto-scroll to bottom when new Q&A appears
  useEffect(() => {
    if (open && chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [open, sectionQs.length]);

  const handleAsk = async () => {
    if (!input.trim() || loading) return;
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/contract-jobs/${jobUuid}/questions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ sectionKey, question: input.trim(), contextText, sectionType })
      });
      const data = await response.json();
      if (data.success && data.question) {
        onNewQuestion(data.question);
        setInput('');
      }
    } catch (err) {
      console.error('Q&A error:', err);
    } finally {
      setLoading(false);
    }
  };

  const hasQA = sectionQs.length > 0;

  return (
    <>
      {/* Trigger button — annotation icon when has Q&A, ? icon when empty */}
      <button
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        title={hasQA ? `${sectionQs.length} question${sectionQs.length > 1 ? 's' : ''} asked` : 'Ask a question about this'}
        style={{
          width: hasQA ? 'auto' : '22px', height: '22px', borderRadius: hasQA ? '11px' : '50%',
          padding: hasQA ? '0 8px' : '0',
          border: hasQA ? '2px solid #1565c0' : '1.5px solid #bbb',
          background: hasQA ? '#1565c0' : 'transparent',
          color: hasQA ? 'white' : '#999',
          cursor: 'pointer', fontSize: hasQA ? '11px' : '12px', fontWeight: '700',
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          gap: '4px', transition: 'all 0.2s', flexShrink: 0
        }}
        onMouseEnter={e => {
          if (!hasQA) { e.currentTarget.style.background = '#e3f2fd'; e.currentTarget.style.color = '#1565c0'; e.currentTarget.style.borderColor = '#1565c0'; }
          else { e.currentTarget.style.background = '#0d47a1'; }
        }}
        onMouseLeave={e => {
          if (!hasQA) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#999'; e.currentTarget.style.borderColor = '#bbb'; }
          else { e.currentTarget.style.background = '#1565c0'; }
        }}
      >
        {hasQA ? (
          <>{'\u{1F4AC}'} {sectionQs.length}</>
        ) : '?'}
      </button>

      {/* Full-screen slide-out panel */}
      {open && (
        <div
          onClick={() => setOpen(false)}
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.4)', zIndex: 1000,
            display: 'flex', justifyContent: 'flex-end',
            animation: 'fadeIn 0.2s ease'
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              width: '560px', maxWidth: '92vw', height: '100%',
              background: 'white', display: 'flex', flexDirection: 'column',
              boxShadow: '-4px 0 20px rgba(0,0,0,0.15)',
              animation: 'slideInRight 0.25s ease'
            }}
          >
            {/* Header */}
            <div style={{
              padding: '16px 20px', borderBottom: '1px solid #e0e0e0',
              background: '#1a237e', color: 'white'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h3 style={{ margin: 0, fontSize: '15px', fontWeight: '600' }}>
                  Q&A{sectionQs.length > 0 ? ` (${sectionQs.length})` : ''}
                </h3>
                <button onClick={() => setOpen(false)} style={{
                  background: 'rgba(255,255,255,0.2)', border: 'none', cursor: 'pointer',
                  color: 'white', fontSize: '18px', width: '28px', height: '28px',
                  borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center'
                }}>&times;</button>
              </div>
              <div style={{ fontSize: '12px', opacity: 0.8, marginTop: '4px' }}>
                {sectionType}
              </div>
            </div>

            {/* Context snippet */}
            {contextText && (
              <div style={{
                padding: '12px 20px', borderBottom: '1px solid #eee', background: '#f8f9fa'
              }}>
                <div style={{ fontSize: '10px', fontWeight: '600', color: '#999', textTransform: 'uppercase', marginBottom: '4px' }}>
                  Context
                </div>
                <div style={{
                  fontSize: '12px', color: '#555', lineHeight: '1.5',
                  maxHeight: '60px', overflow: 'hidden', textOverflow: 'ellipsis',
                  fontStyle: 'italic'
                }}>
                  &ldquo;{contextText.substring(0, 200)}{contextText.length > 200 ? '...' : ''}&rdquo;
                </div>
              </div>
            )}

            {/* Chat-style Q&A thread */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px' }}>
              {sectionQs.length === 0 && (
                <div style={{
                  textAlign: 'center', color: '#999', padding: '40px 20px',
                  fontSize: '13px'
                }}>
                  <div style={{ fontSize: '32px', marginBottom: '12px', opacity: 0.3 }}>?</div>
                  Ask a question about this section of the contract.
                  <br />The AI will answer based on the contract context.
                </div>
              )}
              {sectionQs.map((q, i) => (
                <div key={q.id || i} style={{ marginBottom: '16px' }}>
                  {/* User question */}
                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '6px' }}>
                    <div style={{
                      background: '#1565c0', color: 'white', borderRadius: '14px 14px 2px 14px',
                      padding: '10px 14px', fontSize: '13px', fontWeight: '500',
                      maxWidth: '85%', lineHeight: '1.5'
                    }}>{q.question}</div>
                  </div>
                  {/* AI answer */}
                  <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                    <div
                      style={{
                        background: '#f5f5f5', borderRadius: '14px 14px 14px 2px',
                        padding: '10px 14px', fontSize: '13px', color: '#333',
                        maxWidth: '90%', lineHeight: '1.6'
                      }}
                      dangerouslySetInnerHTML={{ __html: renderMarkdown(q.answer) }}
                    />
                  </div>
                </div>
              ))}
              {loading && (
                <div style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: '16px' }}>
                  <div style={{
                    background: '#f5f5f5', borderRadius: '14px 14px 14px 2px',
                    padding: '10px 14px', fontSize: '13px', color: '#999'
                  }}>
                    Thinking...
                  </div>
                </div>
              )}
              <div ref={chatEndRef} />
            </div>

            {/* Input — always at bottom */}
            <div style={{
              padding: '12px 16px', borderTop: '1px solid #e0e0e0', background: 'white',
              display: 'flex', gap: '8px'
            }}>
              <input
                type="text"
                value={input}
                onChange={e => setInput(e.target.value)}
                placeholder={sectionQs.length > 0 ? 'Ask a follow-up...' : 'Ask about this...'}
                autoFocus
                onKeyDown={e => e.key === 'Enter' && handleAsk()}
                style={{
                  flex: 1, padding: '10px 14px', borderRadius: '22px',
                  border: '1px solid #ddd', fontSize: '13px', outline: 'none'
                }}
              />
              <button
                onClick={handleAsk}
                disabled={loading || !input.trim()}
                style={{
                  padding: '10px 18px', borderRadius: '22px', border: 'none',
                  background: loading || !input.trim() ? '#ccc' : '#1565c0',
                  color: 'white', cursor: loading || !input.trim() ? 'default' : 'pointer',
                  fontSize: '13px', fontWeight: '600', whiteSpace: 'nowrap'
                }}
              >{loading ? '...' : 'Ask'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Slide-in animation styles */}
      {open && (
        <style>{`
          @keyframes slideInRight {
            from { transform: translateX(100%); }
            to { transform: translateX(0); }
          }
          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
        `}</style>
      )}
    </>
  );
}

function SourceTextModal({ isOpen, onClose, title, sourceText }) {
  if (!isOpen) return null;
  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex',
      alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px'
    }} onClick={onClose}>
      <div style={{
        background: 'white', borderRadius: '12px', padding: '28px',
        maxWidth: '600px', width: '100%', maxHeight: '80vh', overflow: 'auto'
      }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
          <h3 style={{ margin: 0, color: '#1a237e', fontSize: '16px' }}>{title}</h3>
          <button onClick={onClose} style={{
            border: 'none', background: 'none', fontSize: '20px', cursor: 'pointer',
            color: '#999', padding: '0 4px', lineHeight: '1'
          }}>&times;</button>
        </div>
        <div style={{
          background: '#f5f5f0', borderLeft: '4px solid #1565c0', padding: '16px',
          borderRadius: '0 8px 8px 0', fontStyle: 'italic', color: '#333',
          lineHeight: '1.7', fontSize: '14px', whiteSpace: 'pre-wrap'
        }}>
          "{sourceText}"
        </div>
      </div>
    </div>
  );
}

function RiskChart({ risks, parties, jobUuid, questions, onNewQuestion }) {
  const [expandedRisk, setExpandedRisk] = useState(null); // index of expanded risk

  // Assign party colors
  const partyColors = ['#1565c0', '#c62828', '#2e7d32', '#e65100', '#6a1b9a', '#00838f'];
  const uniqueParties = [];
  (risks || []).forEach(r => {
    const p = r.affectedParty || 'Unspecified';
    if (!uniqueParties.includes(p)) uniqueParties.push(p);
  });
  const partyColorMap = {};
  uniqueParties.forEach((p, i) => { partyColorMap[p] = partyColors[i % partyColors.length]; });

  // Build a flat numbered list (all risks, sequential)
  const numberedRisks = (risks || []).map((risk, idx) => ({
    ...risk,
    number: idx + 1,
    yScore: risk.severityScore || (risk.severity === 'high' ? 8 : risk.severity === 'medium' ? 5 : 3),
    xScore: risk.actionScore != null ? risk.actionScore : (risk.actionType === 'negotiate' ? 7 : 3),
    party: risk.affectedParty || 'Unspecified',
    actionType: risk.actionType || (risk.actionScore != null ? (risk.actionScore <= 5 ? 'mitigate' : 'negotiate') : null)
  }));

  const handleDotClick = (idx) => {
    setExpandedRisk(expandedRisk === idx ? null : idx);
    // Scroll to the row
    setTimeout(() => {
      const el = document.getElementById(`risk-row-${idx}`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 50);
  };

  return (
    <div>
      {/* 2D Scatter plot: X = Mitigate-Negotiate, Y = Severity */}
      <div style={{
        background: '#fafafa', borderRadius: '12px', padding: '24px',
        border: '1px solid #e0e0e0', marginBottom: '16px'
      }}>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'stretch' }}>
          {/* Y-axis label */}
          <div style={{
            writingMode: 'vertical-rl', transform: 'rotate(180deg)',
            fontSize: '11px', color: '#999', textAlign: 'center', paddingRight: '4px'
          }}>
            Severity Score
          </div>

          {/* Chart area */}
          <div style={{ flex: 1 }}>
            {/* Plot area */}
            <div style={{ position: 'relative', height: '260px', borderLeft: '1px solid #ccc', borderBottom: '1px solid #ccc' }}>
              {/* Horizontal grid lines */}
              {[2, 4, 6, 8, 10].map(v => (
                <div key={v} style={{
                  position: 'absolute', left: 0, right: 0,
                  bottom: `${(v / 10) * 100}%`,
                  borderTop: '1px dashed #e8e8e8'
                }}>
                  <span style={{ position: 'absolute', left: '-24px', top: '-8px', fontSize: '10px', color: '#aaa' }}>{v}</span>
                </div>
              ))}

              {/* Vertical center line */}
              <div style={{
                position: 'absolute', left: '50%', top: 0, bottom: 0,
                borderLeft: '2px dashed #bbb', zIndex: 0
              }} />

              {/* Background zone labels */}
              <div style={{
                position: 'absolute', top: '8px', left: '8px',
                fontSize: '10px', color: '#aaa', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '1px'
              }}>Mitigate</div>
              <div style={{
                position: 'absolute', top: '8px', right: '8px',
                fontSize: '10px', color: '#aaa', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '1px'
              }}>Negotiate</div>

              {/* Background zone tinting */}
              <div style={{
                position: 'absolute', left: 0, top: 0, bottom: 0, width: '50%',
                background: 'rgba(46, 125, 50, 0.03)'
              }} />
              <div style={{
                position: 'absolute', right: 0, top: 0, bottom: 0, width: '50%',
                background: 'rgba(198, 40, 40, 0.03)'
              }} />

              {/* Risk dots — numbered sequentially */}
              {numberedRisks.map((risk, idx) => {
                const dotColor = partyColorMap[risk.party] || '#666';
                const xPercent = 5 + ((risk.xScore - 1) / 9) * 90;
                const yPercent = 5 + ((risk.yScore - 1) / 9) * 90;
                const isExpanded = expandedRisk === idx;

                return (
                  <div
                    key={idx}
                    onClick={() => handleDotClick(idx)}
                    title={`#${risk.number}: ${risk.risk}\nSeverity: ${risk.yScore}/10 | ${risk.actionType || 'unknown'}\nParty: ${risk.party}`}
                    style={{
                      position: 'absolute',
                      bottom: `${yPercent}%`,
                      left: `${xPercent}%`,
                      transform: isExpanded ? 'translate(-50%, 50%) scale(1.4)' : 'translate(-50%, 50%)',
                      width: '28px', height: '28px',
                      borderRadius: '50%',
                      backgroundColor: dotColor,
                      border: isExpanded ? '3px solid #1a237e' : '2px solid white',
                      boxShadow: isExpanded ? '0 0 0 3px rgba(26,35,126,0.3), 0 2px 6px rgba(0,0,0,0.3)' : '0 2px 6px rgba(0,0,0,0.2)',
                      cursor: 'pointer',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: '11px', color: 'white', fontWeight: 'bold',
                      transition: 'transform 0.2s, box-shadow 0.2s, border 0.2s',
                      zIndex: isExpanded ? 100 : idx + 1
                    }}
                    onMouseEnter={e => { if (!isExpanded) e.currentTarget.style.transform = 'translate(-50%, 50%) scale(1.3)'; }}
                    onMouseLeave={e => { if (!isExpanded) e.currentTarget.style.transform = 'translate(-50%, 50%)'; }}
                  >
                    {risk.number}
                  </div>
                );
              })}
            </div>

            {/* X-axis labels */}
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '8px', padding: '0 4px' }}>
              <span style={{ fontSize: '12px', fontWeight: '700', color: '#2e7d32' }}>Mitigate</span>
              <span style={{ fontSize: '11px', color: '#bbb' }}>Action Required</span>
              <span style={{ fontSize: '12px', fontWeight: '700', color: '#c62828' }}>Negotiate</span>
            </div>
          </div>
        </div>

        {/* Party legend */}
        <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', marginTop: '16px', flexWrap: 'wrap' }}>
          {uniqueParties.map((party) => (
            <div key={party} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: '#666' }}>
              <div style={{ width: '12px', height: '12px', borderRadius: '50%', background: partyColorMap[party] }} />
              {party}
            </div>
          ))}
        </div>
        <p style={{ textAlign: 'center', fontSize: '11px', color: '#aaa', marginTop: '8px', marginBottom: 0 }}>
          Click a numbered dot to expand its details below
        </p>
      </div>

      {/* Risk list below chart — numbered to match dots */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {numberedRisks.map((risk, idx) => {
          const isExpanded = expandedRisk === idx;
          const dotColor = partyColorMap[risk.party] || '#666';
          const bgColor = isExpanded ? '#f5f5ff' : (risk.severity === 'high' ? '#fce4ec' : risk.severity === 'medium' ? '#fff3e0' : '#e8f5e9');

          return (
            <div
              key={idx}
              id={`risk-row-${idx}`}
              style={{
                borderRadius: '10px', overflow: 'hidden',
                border: isExpanded ? '2px solid #1a237e' : '1px solid #e0e0e0',
                transition: 'border 0.2s, box-shadow 0.2s',
                boxShadow: isExpanded ? '0 4px 12px rgba(26,35,126,0.15)' : 'none'
              }}
            >
              {/* Row header */}
              <div
                onClick={() => handleDotClick(idx)}
                style={{
                  padding: '12px 16px', background: bgColor,
                  cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '12px',
                  transition: 'background 0.2s'
                }}
                onMouseEnter={e => { if (!isExpanded) e.currentTarget.style.background = '#f0f0f5'; }}
                onMouseLeave={e => { if (!isExpanded) e.currentTarget.style.background = bgColor; }}
              >
                {/* Number badge matching dot */}
                <span style={{
                  width: '28px', height: '28px', borderRadius: '50%',
                  background: dotColor, color: 'white',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '12px', fontWeight: 'bold', flexShrink: 0
                }}>{risk.number}</span>

                <div style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ fontSize: '14px', fontWeight: '500', color: '#333' }}>{risk.risk}</span>
                  <div style={{ display: 'flex', gap: '6px', alignItems: 'center', marginTop: '2px' }}>
                    <span style={{ fontSize: '11px', color: '#888' }}>{risk.party}</span>
                    <span style={{ fontSize: '11px', color: '#ccc' }}>|</span>
                    <span style={{ fontSize: '11px', color: '#888' }}>Severity: {risk.yScore}/10</span>
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexShrink: 0 }}>
                  {/* Annotation badge on collapsed row */}
                  {jobUuid && (() => {
                    const riskQs = (questions || []).filter(q => q.section_key === `risk-${idx}`);
                    return riskQs.length > 0 ? (
                      <span
                        onClick={(e) => e.stopPropagation()}
                        style={{ display: 'inline-flex' }}
                      >
                        <AskQuestionButton
                          sectionKey={`risk-${idx}`}
                          contextText={risk.sourceText || risk.risk}
                          sectionType={`risk: ${risk.risk}`}
                          jobUuid={jobUuid}
                          questions={questions || []}
                          onNewQuestion={onNewQuestion}
                        />
                      </span>
                    ) : null;
                  })()}
                  {risk.actionType && (
                    <span style={{
                      fontSize: '10px', fontWeight: '700', padding: '2px 8px', borderRadius: '10px',
                      color: risk.actionType === 'negotiate' ? '#c62828' : '#2e7d32',
                      background: risk.actionType === 'negotiate' ? '#fce4ec' : '#e8f5e9',
                      border: `1px solid ${risk.actionType === 'negotiate' ? '#ef9a9a' : '#a5d6a7'}`,
                      whiteSpace: 'nowrap', textTransform: 'uppercase'
                    }}>{risk.actionType}</span>
                  )}
                  <span style={{
                    fontSize: '11px', fontWeight: '700', padding: '2px 8px', borderRadius: '10px',
                    color: 'white', whiteSpace: 'nowrap',
                    background: risk.severity === 'high' ? '#c62828' : risk.severity === 'medium' ? '#e65100' : '#2e7d32'
                  }}>{risk.severity}</span>
                  <span style={{ fontSize: '16px', color: '#999', transition: 'transform 0.2s', transform: isExpanded ? 'rotate(180deg)' : 'rotate(0)' }}>
                    &#9660;
                  </span>
                </div>
              </div>

              {/* Expanded detail panel */}
              {isExpanded && (
                <div style={{ padding: '16px 20px', background: 'white', borderTop: '1px solid #e8e8e8' }}>
                  {/* Source text */}
                  {risk.sourceText && (
                    <div style={{ marginBottom: '16px' }}>
                      <div style={{ fontSize: '11px', fontWeight: '600', color: '#999', textTransform: 'uppercase', marginBottom: '6px' }}>
                        Contract Text
                      </div>
                      <div style={{
                        background: '#f5f5f0', borderLeft: '4px solid #1565c0', padding: '12px 16px',
                        borderRadius: '0 8px 8px 0', fontStyle: 'italic', color: '#333',
                        lineHeight: '1.7', fontSize: '13px', whiteSpace: 'pre-wrap'
                      }}>
                        &ldquo;{risk.sourceText}&rdquo;
                      </div>
                    </div>
                  )}

                  {/* Mitigation / Counter-proposal */}
                  {risk.mitigation && (
                    <div style={{ marginBottom: '8px' }}>
                      <div style={{ fontSize: '11px', fontWeight: '600', color: '#999', textTransform: 'uppercase', marginBottom: '6px' }}>
                        {risk.actionType === 'negotiate' ? 'Suggested Counter-Proposal' : 'Mitigation Strategy'}
                      </div>
                      <div style={{
                        background: risk.actionType === 'negotiate' ? '#fff3e0' : '#e8f5e9',
                        borderLeft: `4px solid ${risk.actionType === 'negotiate' ? '#e65100' : '#2e7d32'}`,
                        padding: '12px 16px', borderRadius: '0 8px 8px 0',
                        fontSize: '13px', lineHeight: '1.6', color: '#333'
                      }}>
                        {risk.mitigation}
                      </div>
                    </div>
                  )}

                  {/* Meta info */}
                  <div style={{ display: 'flex', gap: '16px', marginTop: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
                    <div style={{ fontSize: '12px', color: '#666' }}>
                      <strong>Affected Party:</strong> {risk.party}
                    </div>
                    <div style={{ fontSize: '12px', color: '#666' }}>
                      <strong>Severity:</strong> {risk.yScore}/10
                    </div>
                    <div style={{ fontSize: '12px', color: '#666' }}>
                      <strong>Action Score:</strong> {risk.xScore}/10 ({risk.xScore <= 5 ? 'mitigate' : 'negotiate'})
                    </div>
                    {jobUuid && (
                      <div style={{ marginLeft: 'auto' }}>
                        <AskQuestionButton
                          sectionKey={`risk-${idx}`}
                          contextText={risk.sourceText || risk.risk}
                          sectionType={`risk: ${risk.risk}`}
                          jobUuid={jobUuid}
                          questions={questions || []}
                          onNewQuestion={onNewQuestion}
                        />
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function PartyResponsibilities({ obligations, jobUuid, questions, onNewQuestion }) {
  const [modalData, setModalData] = useState(null);
  const partyColors = ['#1565c0', '#c62828', '#2e7d32', '#e65100', '#6a1b9a', '#00838f'];
  const categoryIcons = {
    financial: '$', delivery: 'D', compliance: 'C', reporting: 'R',
    confidentiality: 'L', termination: 'T', other: 'O'
  };

  if (!obligations) return null;
  const entries = Object.entries(obligations);

  return (
    <div>
      <SourceTextModal
        isOpen={!!modalData}
        onClose={() => setModalData(null)}
        title={modalData?.obligation || modalData?.title || ''}
        sourceText={modalData?.sourceText || 'No source text available'}
      />

      <div style={{ display: 'grid', gridTemplateColumns: entries.length > 1 ? 'repeat(auto-fit, minmax(300px, 1fr))' : '1fr', gap: '20px' }}>
        {entries.map(([party, partyObligations], pIdx) => {
          const obList = Array.isArray(partyObligations) ? partyObligations : [];
          const color = partyColors[pIdx % partyColors.length];

          return (
            <div key={party} style={{
              background: 'white', borderRadius: '12px', border: '1px solid #e0e0e0',
              overflow: 'hidden'
            }}>
              {/* Party header */}
              <div style={{
                background: color, color: 'white', padding: '14px 18px',
                display: 'flex', justifyContent: 'space-between', alignItems: 'center'
              }}>
                <h4 style={{ margin: 0, fontSize: '15px', color: 'white', fontWeight: '700' }}>{party}</h4>
                <span style={{
                  background: 'rgba(255,255,255,0.25)', padding: '2px 10px',
                  borderRadius: '10px', fontSize: '12px', fontWeight: '600'
                }}>{obList.length}</span>
              </div>

              {/* Obligation items */}
              <div style={{ padding: '8px' }}>
                {obList.map((ob, idx) => {
                  const isObject = typeof ob === 'object' && ob !== null;
                  const text = isObject ? ob.obligation : ob;
                  const source = isObject ? ob.sourceText : null;
                  const category = isObject ? ob.category : null;

                  return (
                    <div
                      key={idx}
                      onClick={() => source && setModalData({ obligation: text, sourceText: source })}
                      style={{
                        padding: '10px 12px', borderRadius: '8px', marginBottom: '4px',
                        cursor: source ? 'pointer' : 'default',
                        display: 'flex', alignItems: 'flex-start', gap: '10px',
                        transition: 'background-color 0.2s',
                        backgroundColor: 'transparent'
                      }}
                      onMouseEnter={e => { if (source) e.currentTarget.style.backgroundColor = '#f5f5f5'; }}
                      onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                    >
                      {/* Category badge */}
                      <span style={{
                        minWidth: '24px', height: '24px', borderRadius: '6px',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: '11px', fontWeight: '700', color: 'white',
                        background: color, opacity: 0.8, marginTop: '1px'
                      }}>
                        {category ? (categoryIcons[category] || 'O') : (idx + 1)}
                      </span>
                      <div style={{ flex: 1 }}>
                        <span style={{ fontSize: '13px', lineHeight: '1.5' }}>{text}</span>
                        {source && (
                          <span style={{ fontSize: '11px', color: color, display: 'block', marginTop: '2px' }}>
                            View contract text &rarr;
                          </span>
                        )}
                      </div>
                      {jobUuid && (
                        <AskQuestionButton
                          sectionKey={`obligation-${party}-${idx}`}
                          contextText={source || text}
                          sectionType={`obligation of ${party}: ${text}`}
                          jobUuid={jobUuid}
                          questions={questions || []}
                          onNewQuestion={onNewQuestion}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ContractFlowDiagram({ diagramData }) {
  const svgContainerRef = useRef(null);
  const [svgContent, setSvgContent] = useState('');
  const [error, setError] = useState(null);
  const [selectedNode, setSelectedNode] = useState(null);

  const diagram = diagramData?.mermaidDiagram || diagramData;
  const description = diagramData?.diagramDescription || '';
  const nodeDetails = diagramData?.nodeDetails || {};

  // Find detail by matching node ID or label text (case-insensitive partial match)
  const findDetail = (text) => {
    if (!text) return null;
    const cleaned = text.replace(/\s+/g, ' ').trim();
    // Exact match on key
    if (nodeDetails[cleaned]) return { key: cleaned, ...nodeDetails[cleaned] };
    // Case-insensitive match
    const lower = cleaned.toLowerCase();
    for (const [key, val] of Object.entries(nodeDetails)) {
      if (key.toLowerCase() === lower) return { key, ...val };
    }
    // Partial match — node text contained in a key or vice versa
    for (const [key, val] of Object.entries(nodeDetails)) {
      if (key.toLowerCase().includes(lower) || lower.includes(key.toLowerCase())) {
        return { key, ...val };
      }
    }
    // Match against the label field inside details
    for (const [key, val] of Object.entries(nodeDetails)) {
      if (val.label && (val.label.toLowerCase().includes(lower) || lower.includes(val.label.toLowerCase()))) {
        return { key, ...val };
      }
    }
    return null;
  };

  useEffect(() => {
    if (!diagram) return;

    const renderDiagram = async () => {
      try {
        mermaid.initialize({
          startOnLoad: false,
          theme: 'base',
          themeVariables: {
            primaryColor: '#e3f2fd',
            primaryTextColor: '#1a237e',
            primaryBorderColor: '#1565c0',
            lineColor: '#1565c0',
            secondaryColor: '#f3e5f5',
            tertiaryColor: '#e8f5e9',
            fontFamily: 'inherit',
            fontSize: '14px'
          },
          flowchart: { htmlLabels: true, curve: 'basis', padding: 16 },
          securityLevel: 'loose'
        });

        const diagramText = typeof diagram === 'string' ? diagram.replace(/\\n/g, '\n') : '';
        const id = 'contract-flow-' + Math.random().toString(36).substr(2, 9);
        const { svg } = await mermaid.render(id, diagramText);
        setSvgContent(svg);
        setError(null);
      } catch (err) {
        console.error('Mermaid render error:', err);
        setError('Could not render diagram');
      }
    };

    renderDiagram();
  }, [diagram]);

  // Attach click handlers to rendered SVG nodes and edges
  useEffect(() => {
    if (!svgContent || !svgContainerRef.current) return;

    const container = svgContainerRef.current;

    // Make nodes clickable
    const nodes = container.querySelectorAll('.node, .edgeLabel');
    nodes.forEach(node => {
      node.style.cursor = 'pointer';

      const handleClick = (e) => {
        e.stopPropagation();
        // Extract text from node
        const textEl = node.querySelector('span, text, foreignObject div');
        const textContent = textEl ? textEl.textContent.trim() : node.textContent.trim();
        // Also try the node ID (e.g. "flowchart-A-0" -> "A")
        const nodeId = (node.id || '').replace(/^flowchart-/, '').replace(/-\d+$/, '');

        const detail = findDetail(textContent) || findDetail(nodeId);
        if (detail) {
          setSelectedNode({ displayLabel: textContent, ...detail });
        } else {
          setSelectedNode({ displayLabel: textContent, description: `Element: ${textContent}`, label: textContent });
        }
      };

      node.addEventListener('click', handleClick);
      node._flowClickHandler = handleClick;
    });

    // Add hover effect
    nodes.forEach(node => {
      node.addEventListener('mouseenter', () => {
        node.style.filter = 'brightness(0.92) drop-shadow(0 0 4px rgba(21,101,192,0.4))';
      });
      node.addEventListener('mouseleave', () => {
        node.style.filter = 'none';
      });
    });

    return () => {
      nodes.forEach(node => {
        if (node._flowClickHandler) {
          node.removeEventListener('click', node._flowClickHandler);
        }
      });
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [svgContent]);

  if (!diagram) return null;

  return (
    <div>
      {description && (
        <p style={{ fontSize: '13px', color: '#555', lineHeight: '1.6', marginBottom: '16px' }}>{description}</p>
      )}
      <div style={{ fontSize: '11px', color: '#999', marginBottom: '8px', fontStyle: 'italic' }}>
        Click any node or label for details
      </div>
      <div style={{
        background: 'white', borderRadius: '12px', border: '1px solid #e0e0e0',
        padding: '24px', overflow: 'auto'
      }}>
        {error ? (
          <div style={{ color: '#c62828', fontSize: '13px' }}>
            {error}
            <pre style={{
              marginTop: '8px', fontSize: '12px', background: '#f5f5f5',
              padding: '12px', borderRadius: '8px', overflow: 'auto', whiteSpace: 'pre-wrap'
            }}>{typeof diagram === 'string' ? diagram.replace(/\\n/g, '\n') : JSON.stringify(diagram)}</pre>
          </div>
        ) : svgContent ? (
          <div
            ref={svgContainerRef}
            dangerouslySetInnerHTML={{ __html: svgContent }}
            style={{ textAlign: 'center' }}
          />
        ) : (
          <div style={{ textAlign: 'center', color: '#999', padding: '20px' }}>
            Loading diagram...
          </div>
        )}
      </div>

      {/* Node detail modal */}
      {selectedNode && (
        <div
          onClick={() => setSelectedNode(null)}
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1000,
            display: 'flex', alignItems: 'center', justifyContent: 'center'
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: 'white', borderRadius: '14px', padding: '0',
              maxWidth: '560px', width: '90%', maxHeight: '80vh',
              overflow: 'hidden', boxShadow: '0 12px 40px rgba(0,0,0,0.2)',
              display: 'flex', flexDirection: 'column'
            }}
          >
            {/* Modal header */}
            <div style={{
              padding: '16px 20px', background: '#1a237e', color: 'white',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center'
            }}>
              <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600' }}>
                {selectedNode.displayLabel || selectedNode.label || selectedNode.key}
              </h3>
              <button onClick={() => setSelectedNode(null)} style={{
                background: 'rgba(255,255,255,0.2)', border: 'none', cursor: 'pointer',
                color: 'white', fontSize: '18px', width: '28px', height: '28px',
                borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center'
              }}>&times;</button>
            </div>

            {/* Modal body */}
            <div style={{ padding: '20px', overflowY: 'auto' }}>
              {selectedNode.description && (
                <div style={{ marginBottom: '16px' }}>
                  <div style={{ fontSize: '11px', fontWeight: '600', color: '#999', textTransform: 'uppercase', marginBottom: '6px' }}>
                    Description
                  </div>
                  <p style={{ margin: 0, fontSize: '14px', color: '#333', lineHeight: '1.6' }}>
                    {selectedNode.description}
                  </p>
                </div>
              )}

              {selectedNode.sourceText && (
                <div style={{ marginBottom: '16px' }}>
                  <div style={{ fontSize: '11px', fontWeight: '600', color: '#999', textTransform: 'uppercase', marginBottom: '6px' }}>
                    Contract Text
                  </div>
                  <div style={{
                    background: '#f5f5f0', borderLeft: '4px solid #1565c0', padding: '12px 16px',
                    borderRadius: '0 8px 8px 0', fontStyle: 'italic', color: '#333',
                    lineHeight: '1.7', fontSize: '13px'
                  }}>
                    &ldquo;{selectedNode.sourceText}&rdquo;
                  </div>
                </div>
              )}

              {selectedNode.section && (
                <div style={{ marginBottom: '16px' }}>
                  <div style={{ fontSize: '11px', fontWeight: '600', color: '#999', textTransform: 'uppercase', marginBottom: '6px' }}>
                    Contract Section
                  </div>
                  <span style={{
                    fontSize: '13px', padding: '4px 12px', borderRadius: '8px',
                    background: '#e3f2fd', color: '#1565c0', fontWeight: '500'
                  }}>{selectedNode.section}</span>
                </div>
              )}

              {!selectedNode.description && !selectedNode.sourceText && (
                <p style={{ color: '#999', fontSize: '13px' }}>
                  No additional details available for this element.
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function WorstCaseScenarios({ scenarios, jobUuid, questions, onNewQuestion }) {
  const [expandedParty, setExpandedParty] = useState(null);
  const [modalData, setModalData] = useState(null);
  const partyColors = ['#c62828', '#1565c0', '#2e7d32', '#e65100', '#6a1b9a', '#00838f'];
  const likelihoodColors = { high: '#c62828', medium: '#e65100', low: '#2e7d32' };

  const entries = Object.entries(scenarios);

  return (
    <div>
      <SourceTextModal
        isOpen={!!modalData}
        onClose={() => setModalData(null)}
        title={modalData?.scenario || ''}
        sourceText={modalData?.sourceText || 'No source text available'}
      />

      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {entries.map(([party, partyScenarios], pIdx) => {
          const color = partyColors[pIdx % partyColors.length];
          const scList = Array.isArray(partyScenarios) ? partyScenarios : [];
          const isExpanded = expandedParty === pIdx;

          return (
            <div key={party} style={{
              borderRadius: '12px', overflow: 'hidden',
              border: '1px solid #e0e0e0', background: 'white'
            }}>
              {/* Party header - clickable */}
              <div
                onClick={() => setExpandedParty(isExpanded ? null : pIdx)}
                style={{
                  background: color, color: 'white', padding: '14px 18px',
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  cursor: 'pointer'
                }}
              >
                <h4 style={{ margin: 0, fontSize: '15px', color: 'white', fontWeight: '700' }}>{party}</h4>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <span style={{
                    background: 'rgba(255,255,255,0.25)', padding: '2px 10px',
                    borderRadius: '10px', fontSize: '12px', fontWeight: '600'
                  }}>{scList.length} scenario{scList.length !== 1 ? 's' : ''}</span>
                  <span style={{
                    fontSize: '16px', transition: 'transform 0.2s',
                    transform: isExpanded ? 'rotate(180deg)' : 'rotate(0)'
                  }}>&#9660;</span>
                </div>
              </div>

              {/* Scenario list */}
              {isExpanded && (
                <div style={{ padding: '8px' }}>
                  {scList.map((sc, idx) => {
                    const lColor = likelihoodColors[sc.likelihood] || '#666';
                    return (
                      <div key={idx} style={{
                        padding: '14px 16px', borderRadius: '10px', marginBottom: '6px',
                        border: '1px solid #f0f0f0', background: '#fafafa',
                        transition: 'background 0.2s'
                      }}
                        onMouseEnter={e => { e.currentTarget.style.background = '#f5f5f5'; }}
                        onMouseLeave={e => { e.currentTarget.style.background = '#fafafa'; }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px' }}>
                          <div style={{ flex: 1 }}>
                            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '6px' }}>
                              <span style={{
                                width: '24px', height: '24px', borderRadius: '50%',
                                background: color, color: 'white', display: 'flex',
                                alignItems: 'center', justifyContent: 'center',
                                fontSize: '11px', fontWeight: 'bold', flexShrink: 0
                              }}>{idx + 1}</span>
                              <span style={{ fontSize: '14px', fontWeight: '600', color: '#333' }}>{sc.scenario}</span>
                            </div>

                            {sc.trigger && (
                              <div style={{ marginBottom: '6px', paddingLeft: '32px' }}>
                                <span style={{ fontSize: '11px', fontWeight: '600', color: '#999', textTransform: 'uppercase' }}>Trigger: </span>
                                <span style={{ fontSize: '13px', color: '#555' }}>{sc.trigger}</span>
                              </div>
                            )}

                            {sc.consequence && (
                              <div style={{
                                marginLeft: '32px', marginBottom: '8px', padding: '10px 14px',
                                background: '#fff3e0', borderLeft: `4px solid ${color}`,
                                borderRadius: '0 8px 8px 0', fontSize: '13px', color: '#333', lineHeight: '1.5'
                              }}>
                                {sc.consequence}
                              </div>
                            )}

                            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', paddingLeft: '32px' }}>
                              {sc.likelihood && (
                                <span style={{
                                  fontSize: '10px', fontWeight: '700', padding: '2px 8px', borderRadius: '10px',
                                  color: 'white', background: lColor, textTransform: 'uppercase'
                                }}>{sc.likelihood} likelihood</span>
                              )}
                              {sc.sourceText && (
                                <span
                                  onClick={() => setModalData(sc)}
                                  style={{ fontSize: '11px', color: '#1565c0', cursor: 'pointer' }}
                                >
                                  View contract text &rarr;
                                </span>
                              )}
                            </div>
                          </div>

                          {jobUuid && (
                            <AskQuestionButton
                              sectionKey={`worstcase-${party}-${idx}`}
                              contextText={sc.sourceText || sc.scenario}
                              sectionType={`worst-case scenario for ${party}: ${sc.scenario}`}
                              jobUuid={jobUuid}
                              questions={questions || []}
                              onNewQuestion={onNewQuestion}
                            />
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ContractAnalysisView({ data, jobUuid }) {
  const [clauseModal, setClauseModal] = useState(null);
  const [questions, setQuestions] = useState([]);

  // Fetch existing Q&A for this job
  useEffect(() => {
    if (!jobUuid) return;
    fetch(`${API_URL}/api/contract-jobs/${jobUuid}/questions`, { credentials: 'include' })
      .then(r => r.json())
      .then(d => { if (d.success) setQuestions(d.questions || []); })
      .catch(() => {});
  }, [jobUuid]);

  const handleNewQuestion = (q) => {
    setQuestions(prev => [...prev, q]);
  };

  const qProps = (sectionKey, contextText, sectionType) => ({
    sectionKey, contextText, sectionType, jobUuid, questions, onNewQuestion: handleNewQuestion
  });

  if (!data) return <p>No analysis data available.</p>;
  // Only fall back to raw text when we have NOTHING else to render.
  const hasStructured = data.summary || data.parties || data.contractType ||
    data.keyClauses || data.risks || data.obligations ||
    data.worstCaseScenarios || data.mermaidDiagram;
  if (data.rawAnalysis && !hasStructured) {
    return <pre className="raw-analysis">{data.rawAnalysis}</pre>;
  }

  return (
    <div className="contract-analysis">
      <SourceTextModal
        isOpen={!!clauseModal}
        onClose={() => setClauseModal(null)}
        title={clauseModal?.clause || ''}
        sourceText={clauseModal?.sourceText || 'No source text available'}
      />

      {/* Summary + Type + Parties overview card */}
      {(data.summary || data.contractType || data.parties) && (
        <section style={{
          background: 'linear-gradient(135deg, #1a237e 0%, #1565c0 100%)',
          borderRadius: '12px', padding: '24px', color: 'white', marginBottom: '24px'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              {data.contractType && (
                <span style={{
                  background: 'rgba(255,255,255,0.2)', padding: '4px 12px',
                  borderRadius: '16px', fontSize: '12px', fontWeight: '600', marginBottom: '12px',
                  display: 'inline-block'
                }}>{data.contractType}</span>
              )}
            </div>
            {jobUuid && (
              <AskQuestionButton {...qProps('summary', data.summary || '', 'contract summary')} />
            )}
          </div>
          {data.summary && <p style={{ margin: '12px 0 0 0', lineHeight: '1.7', opacity: 0.95, fontSize: '14px' }}>{data.summary}</p>}
          {data.parties && data.parties.length > 0 && (
            <div style={{ display: 'flex', gap: '8px', marginTop: '16px', flexWrap: 'wrap' }}>
              {data.parties.map((party, idx) => (
                <span key={idx} style={{
                  background: 'rgba(255,255,255,0.15)', padding: '4px 14px',
                  borderRadius: '20px', fontSize: '13px', fontWeight: '500',
                  border: '1px solid rgba(255,255,255,0.3)'
                }}>{party}</span>
              ))}
            </div>
          )}
        </section>
      )}

      {/* Key Clauses - clickable cards */}
      {data.keyClauses && data.keyClauses.length > 0 && (
        <section style={{ marginBottom: '28px' }}>
          <h3 style={{ color: '#1a237e', borderBottom: '2px solid #1a237e', paddingBottom: '8px', display: 'inline-block' }}>
            Key Clauses
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '12px', marginTop: '12px' }}>
            {data.keyClauses.map((clause, idx) => {
              const impColor = clause.importance === 'high' ? '#c62828' : clause.importance === 'medium' ? '#e65100' : '#2e7d32';
              return (
                <div
                  key={idx}
                  style={{
                    padding: '16px', borderRadius: '10px', border: '1px solid #e0e0e0',
                    transition: 'box-shadow 0.2s, transform 0.2s',
                    background: 'white'
                  }}
                  onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                  onMouseLeave={e => { e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.transform = 'translateY(0)'; }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                    <h4 style={{ margin: 0, fontSize: '14px', color: '#1a237e', flex: 1 }}>{clause.clause}</h4>
                    <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexShrink: 0 }}>
                      {jobUuid && <AskQuestionButton {...qProps(`clause-${idx}`, clause.sourceText || clause.description, `key clause: ${clause.clause}`)} />}
                      <span style={{
                        fontSize: '10px', fontWeight: '700', padding: '2px 8px',
                        borderRadius: '10px', color: 'white', background: impColor,
                        whiteSpace: 'nowrap'
                      }}>{clause.importance}</span>
                    </div>
                  </div>
                  <p style={{ margin: 0, fontSize: '13px', color: '#555', lineHeight: '1.5' }}>{clause.description}</p>
                  {clause.sourceText && (
                    <span
                      onClick={() => setClauseModal(clause)}
                      style={{ fontSize: '11px', color: '#1565c0', marginTop: '8px', display: 'block', cursor: 'pointer' }}
                    >
                      View contract text &rarr;
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* Party Responsibilities */}
      {data.obligations && Object.keys(data.obligations).length > 0 && (
        <section style={{ marginBottom: '28px' }}>
          <h3 style={{ color: '#1a237e', borderBottom: '2px solid #1a237e', paddingBottom: '8px', display: 'inline-block' }}>
            Party Responsibilities
          </h3>
          <div style={{ marginTop: '12px' }}>
            <PartyResponsibilities obligations={data.obligations} jobUuid={jobUuid} questions={questions} onNewQuestion={handleNewQuestion} />
          </div>
        </section>
      )}

      {/* Contract Flow Diagram */}
      {data.mermaidDiagram && (
        <section style={{ marginBottom: '28px' }}>
          <h3 style={{ color: '#1a237e', borderBottom: '2px solid #1a237e', paddingBottom: '8px', display: 'inline-block' }}>
            Contract Flow
          </h3>
          <div style={{ marginTop: '12px' }}>
            <ContractFlowDiagram diagramData={data} />
          </div>
        </section>
      )}

      {/* Risk Assessment Chart */}
      {data.risks && data.risks.length > 0 && (
        <section style={{ marginBottom: '28px' }}>
          <h3 style={{ color: '#1a237e', borderBottom: '2px solid #1a237e', paddingBottom: '8px', display: 'inline-block' }}>
            Risk Assessment
          </h3>
          <div style={{ marginTop: '12px' }}>
            <RiskChart risks={data.risks} parties={data.parties} jobUuid={jobUuid} questions={questions} onNewQuestion={handleNewQuestion} />
          </div>
        </section>
      )}

      {/* Worst Case Scenarios */}
      {data.worstCaseScenarios && Object.keys(data.worstCaseScenarios).length > 0 && (
        <section style={{ marginBottom: '28px' }}>
          <h3 style={{ color: '#1a237e', borderBottom: '2px solid #1a237e', paddingBottom: '8px', display: 'inline-block' }}>
            What Could Go Wrong
          </h3>
          <div style={{ marginTop: '12px' }}>
            <WorstCaseScenarios scenarios={data.worstCaseScenarios} jobUuid={jobUuid} questions={questions} onNewQuestion={handleNewQuestion} />
          </div>
        </section>
      )}

      {/* Recommendations */}
      {data.recommendations && data.recommendations.length > 0 && (
        <section style={{ marginBottom: '28px' }}>
          <h3 style={{ color: '#1a237e', borderBottom: '2px solid #1a237e', paddingBottom: '8px', display: 'inline-block' }}>
            Recommendations
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '12px' }}>
            {data.recommendations.map((rec, idx) => (
              <div key={idx} style={{
                padding: '12px 16px', borderRadius: '8px',
                background: '#e8f5e9', borderLeft: '4px solid #2e7d32',
                fontSize: '13px', lineHeight: '1.5', color: '#333',
                display: 'flex', gap: '10px', alignItems: 'flex-start'
              }}>
                <span style={{
                  minWidth: '22px', height: '22px', borderRadius: '50%',
                  background: '#2e7d32', color: 'white', display: 'flex',
                  alignItems: 'center', justifyContent: 'center',
                  fontSize: '11px', fontWeight: '700'
                }}>{idx + 1}</span>
                <span style={{ flex: 1 }}>{rec}</span>
                {jobUuid && <AskQuestionButton {...qProps(`rec-${idx}`, rec, `recommendation: ${rec}`)} />}
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

export default ContractAnalysis;
